import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Card,
  Button,
  Form,
  message,
  Spin,
  Tooltip,
  Modal,
  Pagination,
  Image,
  Table,
  Tag,
} from "antd";

import Utils from "../../helpers/Utility";
import rules from "../../screens/issue-tracker-details/issue-tracker-validation";
import {
  PlusOutlined,
  UserOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import noAttachmentYet from "../../assets/images/no_attachment_yet.png";
import noResultsImage from "../../assets/images/undefined_image.jpg";
import Meta from "antd/lib/card/Meta";
import issueTrackerServices from "../../services/issueTrackerServices";
import AttachmentUpload from "./attachmentUpload";
import constants from "../../config/constants";
import authStore from "../../store/authStore";
import ConfirmationModal from "../confirmation-modal";
import { useHistory } from "react-router-dom";
import Routes from "../../routing/routes";
import { goToInsightsTicketsList } from "../../helpers/insightsTicketsNav";
import moment from "moment";
import Permissions from "../../config/permssions";

const { Option } = Select;

const priority = [
  { _id: "HIGH", name: "High" },
  { _id: "LOW", name: "Low" },
  { _id: "MEDIUM", name: "Medium" },
];

const TICKET_STATUS_OPTIONS = [
  { value: "OPEN", label: "Open" },
  { value: "INCOMPLETE", label: "Incomplete" },
  { value: "PENDING REVIEW", label: "Pending for Review" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "CLOSED", label: "Closed" },
  { value: "IGNORED", label: "Ignored" },
  { value: "MISSED", label: "Missed" },
];

interface Props {
  issueDetails?: any;
  onCreateTicket: (values: any) => void;
  onClickEdit: (values: any, id: any) => void;
  onClickCancel: () => void;
  onDelete: () => void;
  issueId?: any;
  isLoading: boolean;
  org_id?: any;
  isEditReq?: boolean;
  contractorList?: any
}

const IssueTicketDetails: React.FC<Props> = ({
  issueDetails,
  onCreateTicket,
  onClickEdit,
  onClickCancel,
  onDelete,
  issueId,
  isLoading,
  org_id,
  isEditReq,
  contractorList
}) => {
  const [form] = Form.useForm();
  const [attachmentNote, setAttachmentNote] = useState<any>([]);
  const [imageList, setImageList] = useState<any>([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [attachmentsIndex, setAttachmentsIndex] = useState<any>(0);
  const [storedImageIndex, setStoredImageIndex] = useState<any>(0);
  const [storedNoteIndex, setStoredNoteIndex] = useState<any>(0);
  const [innerImageLength, setInnerImageLength] = useState<any>([]);
  const [innerImageIndex, setInnerImageIndex] = useState<any>(0);
  const [fieldSaved, setFieldSaved] = useState(false);
  const [seeMoreNote, setSeeMoreNote] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showToolTip, setShowToolTip] = useState(false);
  const [openTicketsModalVisible, setOpenTicketsModalVisible] = useState(false);
  const [openTickets, setOpenTickets] = useState<any[]>([]);
  const [openTicketsLoading, setOpenTicketsLoading] = useState(false);
  const history = useHistory();
  const canAddTicketAttachments = authStore?.currentUser?.access?.includes(
    Permissions.create_edit_attachment_issue_tracker
  );
  
  const [fieldValues, setFieldValues] = useState({
    issue_title: "",
    issue_priority: "",
    issue_description: "",
    contractor: "",
    components: "",
    recommended_action: "",
    timeframe: "",
    diagnostic_code: "",
    category: constants.defaultIssueTrackerCategory,
    created_on: Date.now(),
    planned_completion_date: null as number | null,
  });
  const [previousFieldValues, setPreviousFieldValues] = useState({
    issue_title: "",
    issue_priority: "",
    issue_description: "",
    contractor: "",
    components: "",
    recommended_action: "",
    timeframe: "",
    diagnostic_code: "",
    category: constants.defaultIssueTrackerCategory,
    created_on: Date.now(),
    planned_completion_date: null as number | null,
  });

  const getPriorityFromColor = (color: string) => {
    switch(color) {
      case 'red':
      case 'darkred':
        return 'HIGH';
      case 'orange':
        return 'MEDIUM';
      case 'yellow':
        return 'LOW';
      default:
        return 'LOW';
    }
  };

  const disableFutureDate = (current: any) => {
    return current && current > moment().endOf("day");
  };

  const disableFutureTime = (current: any) => {
    if (!current || !current.isSame(moment(), "day")) {
      return {};
    }
    const now = moment();
    const currentHour = now.hour();
    const currentMinute = now.minute();
    return {
      disabledHours: () =>
        Array.from({ length: 24 }, (_, h) => h).filter((h) => h > currentHour),
      disabledMinutes: (selectedHour: number) => {
        if (selectedHour !== currentHour) return [];
        return Array.from({ length: 60 }, (_, m) => m).filter((m) => m > currentMinute);
      },
    };
  };

  const disablePlannedCompletionDate = (current: any) => {
    if (!current) return false;
    const createdOn = form.getFieldValue("created_on");
    if (!createdOn) return false;
    const createdDate = moment(createdOn).startOf("day");
    return current < createdDate;
  };

  useEffect(() => {
    if (issueDetails?.hasOwnProperty("issue_id")) {
      autoPopulateValues(issueDetails);
      setFieldValues({
        issue_title: issueDetails?.issue_title,
        issue_priority: issueDetails?.issue_priority,
        contractor: issueDetails?.contractor?._id || "",
        issue_description: issueDetails?.issue_description
          ? issueDetails?.issue_description
          : "",
        components: issueDetails?.components
          ? issueDetails?.components
          : "",
        recommended_action: issueDetails?.recommended_action
          ? issueDetails?.recommended_action
          : "",
        timeframe: issueDetails?.timeframe
          ? issueDetails?.timeframe
          : "",
        diagnostic_code: issueDetails?.alert_code || "",
        category: issueDetails?.category || constants.defaultIssueTrackerCategory,
        created_on: issueDetails?.created_on || Date.now(),
        planned_completion_date: issueDetails?.planned_completion_date || null,
      });
      setPreviousFieldValues({
        issue_title: issueDetails?.issue_title,
        issue_priority: issueDetails?.issue_priority,
        contractor: issueDetails?.contractor?._id || "",
        issue_description: issueDetails?.issue_description
          ? issueDetails?.issue_description
          : "",
        components: issueDetails?.components
          ? issueDetails?.components
          : "",
        recommended_action: issueDetails?.recommended_action
          ? issueDetails?.recommended_action
          : "",
        timeframe: issueDetails?.timeframe
          ? issueDetails?.timeframe
          : "",
        diagnostic_code: issueDetails?.alert_code || "",
        category: issueDetails?.category || constants.defaultIssueTrackerCategory,
        created_on: issueDetails?.created_on || Date.now(),
        planned_completion_date: issueDetails?.planned_completion_date || null,
      });
    } else {
      const alertDescription = issueDetails?.alert_description || "";
      const trimmedTitle =
        alertDescription.length > 100
          ? alertDescription.substring(0, 100)
          : alertDescription;

      const issueDescription =
        alertDescription.length > 100
          ? alertDescription
          : issueDetails?.issue_description || "";

      setFieldValues({
        issue_title: trimmedTitle,
        issue_priority: getPriorityFromColor(issueDetails?.alert_color),
        contractor: "",
        issue_description: issueDescription,
        components: issueDetails?.components || "",
        recommended_action: issueDetails?.recommended_action || "",
        timeframe: issueDetails?.timeframe || "",
        diagnostic_code: issueDetails?.alert_code || "",
        category: issueDetails?.category || constants.defaultIssueTrackerCategory,
        created_on: Date.now(),
        planned_completion_date: null,
      });

      form.setFieldsValue({
        issue_title: trimmedTitle,
        contractor: undefined,
        created_on: moment(),
        diagnostic_code: issueDetails?.alert_code,
        category: issueDetails?.category || constants.defaultIssueTrackerCategory,
        issue_priority: getPriorityFromColor(issueDetails?.alert_color),
        issue_description: issueDescription
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueDetails]);

  const onFinish = (values: any) => {
    onCreateTicket({
      ...values,
      created_on: values?.created_on ? moment(values?.created_on).valueOf() : Date.now(),
      planned_completion_date: values?.planned_completion_date
        ? moment(values.planned_completion_date).valueOf()
        : null,
    });
  };

  const autoPopulateValues = async (data: any) => {
    form.setFieldsValue({
      issue_title: data?.issue_title || data?.alert_description,
      issue_priority: data?.issue_priority || getPriorityFromColor(data?.alert_color),
      issue_description: data?.issue_description || data?.alert_description,
      contractor: data?.contractor?._id ? (contractorList?.find((element: { _id: string; name: string}) => element?._id === data?.contractor?._id) ? data?.contractor?._id : undefined) : undefined,
      components: data?.components,
      recommended_action: data?.recommended_action,
      timeframe: data?.timeframe,
      created_on: data?.created_on ? moment(data?.created_on) : moment(),
      diagnostic_code: data?.alert_code,
      category: data?.category || constants.defaultIssueTrackerCategory,
      planned_completion_date: data?.planned_completion_date ? moment(data.planned_completion_date) : undefined,
      ticket_status: data?.status,
      status_effective_date: data?.status_updated_at ? moment(data.status_updated_at) : moment(),
    });
  };

  const handleEdit = () => {
    const values = form.getFieldsValue();
    const { ticket_status, status_effective_date, planned_completion_date, ...rest } = values;
    onClickEdit(
      {
        ...rest,
        created_on: values?.created_on ? moment(values?.created_on).valueOf() : Date.now(),
        planned_completion_date: planned_completion_date
          ? moment(planned_completion_date).valueOf()
          : null,
        status: authStore?.isOrgAdmin() ? ticket_status : undefined,
        status_updated_at:
          authStore?.isOrgAdmin() && status_effective_date
            ? moment(status_effective_date).valueOf()
            : undefined,
      },
      id
    );
  };

  const handleCancel = () => {
    onClickCancel();
  };

  const handleBack = () => {
    goToInsightsTicketsList(history);
  };

  const handleDelete = () => {
    onDelete();
  };

  const handleSaveTicket = () => {
    try {
        form.submit();
      if (
        form.getFieldValue("issue_title") &&
        form.getFieldValue("issue_priority")
      ) {
        
        setFieldSaved(true);
        setFieldValues({
          issue_title: form.getFieldValue("issue_title"),
          contractor: form.getFieldValue("contractor"),
          issue_priority: form.getFieldValue("issue_priority"),
          issue_description: form.getFieldValue("issue_description")
            ? form.getFieldValue("issue_description")
            : "",
          components: form.getFieldValue("components")
            ? form.getFieldValue("components")
            : "",
          recommended_action: form.getFieldValue("recommended_action")
            ? form.getFieldValue("recommended_action")
            : "",
          timeframe: form.getFieldValue("timeframe")
            ? form.getFieldValue("timeframe")
            : "",
          diagnostic_code: form.getFieldValue("diagnostic_code") || "",
          category: form.getFieldValue("category") || constants.defaultIssueTrackerCategory,
          created_on: form.getFieldValue("created_on")
            ? moment(form.getFieldValue("created_on")).valueOf()
            : Date.now(),
          planned_completion_date: form.getFieldValue("planned_completion_date")
            ? moment(form.getFieldValue("planned_completion_date")).valueOf()
            : null,
        });
        setPreviousFieldValues({
          issue_title: form.getFieldValue("issue_title"),
          contractor: form.getFieldValue("contractor"),
          issue_priority: form.getFieldValue("issue_priority"),
          issue_description: form.getFieldValue("issue_description")
            ? form.getFieldValue("issue_description")
            : "",
          components: form.getFieldValue("components")
            ? form.getFieldValue("components")
            : "",
          recommended_action: form.getFieldValue("recommended_action")
            ? form.getFieldValue("recommended_action")
            : "",
          timeframe: form.getFieldValue("timeframe")
            ? form.getFieldValue("timeframe")
            : "",
          diagnostic_code: form.getFieldValue("diagnostic_code") || "",
          category: form.getFieldValue("category") || constants.defaultIssueTrackerCategory,
          created_on: form.getFieldValue("created_on")
            ? moment(form.getFieldValue("created_on")).valueOf()
            : Date.now(),
          planned_completion_date: form.getFieldValue("planned_completion_date")
            ? moment(form.getFieldValue("planned_completion_date")).valueOf()
            : null,
        });
      }
    } catch (error) {
      message.destroy()
      message.error(constants.message.internalServerError)
    }
  };

  const fetchIssueDetailsWithId = async () => {
    try {
      let attachmentNotes: any[] = [];
      let imageListTotalLength: any[] = [];
      if (
        (issueId || issueDetails?._id || id) &&
        (org_id || issueDetails?.org_id)
      ) {
        setImageLoading(true);
        const issuesDetails =
          await issueTrackerServices.IssueTrackerDetailsById(
            issueId ? issueId : issueDetails?._id,
            org_id ? org_id : issueDetails?.org_id
          );
        setTicketId(issuesDetails?.issue?.issue_id);
        setId(issuesDetails?.issue?._id);

        if (
          !issuesDetails?.issue ||
          !issuesDetails?.issue?.notes ||
          !Array.isArray(issuesDetails?.issue.notes)
        ) {
          setImageLoading(false);
          return [];
        }
        setAttachmentsIndex(issuesDetails?.issue?.notes?.length);
        let attachmentsResponse = issuesDetails?.issue?.notes?.map(
          async (note: any) => {
            attachmentNotes.push(note?.text);
            if (note?.attachments?.length) {
              let res: any = await Promise.all(
                (note?.attachments || []).map(async (element: any) => {
                  return await issueTrackerServices.getAttachedImageByimageId(
                    element
                  );
                })
              );
              let urls = res?.map((item: any) => {
                let url = URL.createObjectURL(item?.data);
                return url;
              });

              return urls;
            }
          }
        );
        const responses: any = await Promise.all(attachmentsResponse);
        responses?.forEach((resDatas: any[]) => {
          imageListTotalLength.push(resDatas?.length);
        });
        setInnerImageLength(imageListTotalLength);
        setImageList(responses);
        setAttachmentNote(attachmentNotes);
        setShowToolTip(true)
        setTimeout(() => {
            setShowToolTip(false);
          }, 4000);
        setImageLoading(false);
      } else {
        setImageLoading(false);
      }
    } catch (error: any) {
      setImageLoading(false);
      const errorData = error?.response?.data
        const isUnauthorizedIssue = Utils.issueTrackerErrorHandler(errorData, constants.responseCode.unAuthorized, constants.message.issue_allocation_changed, constants.message.issue_doesnot_exist)
        if (isUnauthorizedIssue) {
            message.destroy()
            await message.error(error?.response?.data?.message)
            goToInsightsTicketsList(history);
        } else {
            message.destroy()
            message.error(error?.response?.data?.message || constants.message.internalServerError)
        }
    }
  };

  useEffect(() => {
    fetchIssueDetailsWithId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId, org_id, issueDetails?._id, issueDetails?.org_id]);

  const handleFieldChange = (fieldName: any, value: any) => {
    if (value === constants.addNewContractor) {
      history.push({
        pathname: `${Routes.users}`,
        state: {
          shouldBeVisible: true,
        },
      });
    } else {
    setFieldValues((prevData) => ({
      ...prevData,
      [fieldName]: value || "",
    }));
  }
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then(() => {
        setIsVisible(true);
      })
      .catch((errorInfo) => {
      });
  };

  const seeMore = (note: any) => {
    return (
      <>
        {note}......
        <Button type="link" size="small" onClick={() => setSeeMoreNote(true)}>
          see more
        </Button>
      </>
    );
  };

  const handleViewOpenTickets = async () => {
    setOpenTicketsModalVisible(true);
    setOpenTicketsLoading(true);
    try {
      const group_id = issueDetails?.group_id?._id || issueDetails?.group_id;
      const sensorbox_id = issueDetails?.sensor_box?._id || issueDetails?.sensor_box;
      const response = await issueTrackerServices.getOpenTicketsByGroupOrSensorBox(
        group_id,
        sensorbox_id,
        org_id || issueDetails?.org_id
      );
      setOpenTickets(response?.issues || []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || constants.message.internalServerError);
    } finally {
      setOpenTicketsLoading(false);
    }
  };

  const openTicketsColumns = [
    {
      title: 'Ticket ID',
      dataIndex: 'issue_id',
      key: 'issue_id',
      ellipsis: {
        showTitle: false,
      },
      render: (name: any) => (
        <Tooltip placement="topLeft" title={name}>
          {name || '-'}
        </Tooltip>
      ),
      width: '13%',
      align: 'center' as const,
    },
    {
      title: 'Title',
      dataIndex: 'issue_title',
      key: 'issue_title',
      ellipsis: {
        showTitle: false,
      },
      render: (name: any) => (
        <Tooltip placement="topLeft" title={name}>
          {name || '-'}
        </Tooltip>
      ),
      width: '64%',
      align: 'center' as const,
    },
    // {
    //   title: 'Status',
    //   key: 'status',
    //   dataIndex: 'status',
    //   render: (data: any) => (
    //     <Tag color={Utils.getIssueTrackerStatus(data)} key={data}>
    //       {data?.toUpperCase()}
    //     </Tag>
    //   ),
    //   width: '10%',
    //   align: 'left' as const,
    // },
    {
      title: 'Created On',
      key: 'created_on',
      dataIndex: 'created_on',
      render: (timestamp: any) => timestamp ? new Date(timestamp).toLocaleDateString() : '-',
      width: '13%',
      align: 'center' as const,
    },
    {
      title: 'Last Updated',
      key: 'updated_on',
      dataIndex: 'updated_on',
      render: (timestamp: any) => timestamp ? new Date(timestamp).toLocaleDateString() : '-',
      width: '13%',
      align: 'center' as const,
    },
  ];

  const openTicketsTableData = openTickets?.map((issue: any, index: number) => {
    return {
      key: index + 1,
      _id: issue?._id,
      issue_id: issue?.issue_id,
      issue_title: issue?.issue_title,
      // status: issue?.status,
      created_on: issue?.created_on,
      updated_on: issue?.updated_on,
    };
  });

  return (
    <>
      <Col span={18}>
        <Card style={{height:880}}
          className="custom-card online-device border-0 border-1"
          title={
            <div className="card-title-wrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{issueDetails.system_name}</span>
                <div
                  className={`small-circle-alert ml-1 ${Utils.getStatus(
                    Number(issueDetails?.alert_status)
                  )}`}
                ></div>
              </div>
              {!id && (
                <Button type="link" className="issue-ticket-link-btn" onClick={handleViewOpenTickets} style={{ padding: 0, fontWeight: 600 }}>
                  View Open Tickets
                </Button>
              )}
            </div>
          }
        >
          <Spin spinning={isLoading ? isLoading : loading}>
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              onFinish={onFinish}
            >
                <Col span={24}>
                  <Form.Item
                    normalize={Utils.trimSpacesInValues}
                    name="issue_title"
                    label="Ticket Title"
                    rules={rules?.issue_title}
                  >
                    <Input
                      onChange={(e) =>
                        handleFieldChange("issue_title", e.target?.value)
                      }
                      maxLength={100}
                      showCount
                      placeholder="Enter Title"
                      className="custom-input"
                    />
                  </Form.Item>
                </Col>

              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Form.Item
                    name="created_on"
                    label="Date Time"
                    rules={[{ required: true, message: "Date Time is required" }]}
                    initialValue={moment()}
                  >
                    <DatePicker
                      className="w-100 issue-date-time-picker"
                      showTime={{ format: "HH:mm" }}
                      format="MM/DD/YYYY HH:mm"
                      allowClear={false}
                      disabledDate={disableFutureDate}
                      disabledTime={disableFutureTime}
                      popupClassName="issue-ticket-datetime-popup"
                      getPopupContainer={(trigger) => trigger.parentElement || document.body}
                      onChange={(value) =>
                        handleFieldChange(
                          "created_on",
                          value ? moment(value).valueOf() : Date.now()
                        )
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="planned_completion_date" label="Planned Completion Date">
                    <DatePicker
                      className="w-100 issue-date-time-picker"
                      format="MM/DD/YYYY"
                      allowClear
                      disabledDate={disablePlannedCompletionDate}
                      popupClassName="issue-ticket-datetime-popup"
                      getPopupContainer={(trigger) => trigger.parentElement || document.body}
                      onChange={(value) =>
                        handleFieldChange(
                          "planned_completion_date",
                          value ? moment(value).valueOf() : null
                        )
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                      name="issue_priority"
                      label="Ticket Priority"
                      rules={rules.issue_priority}
                    >
                      <Select
                        onChange={(value) =>
                          handleFieldChange("issue_priority", value)
                        }
                        className="slct_style"
                        placeholder="Choose Priority"
                        disabled={false}
                        getPopupContainer={(trigger) => trigger.parentElement || document.body}
                        dropdownStyle={{ zIndex: 2100 }}
                      >
                        {priority &&
                          priority.map((data: any) => {
                            return <Option key={data._id} value={data._id}>{data?.name}</Option>;
                          })}
                      </Select>
                    </Form.Item>
                  </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col span={authStore?.isOrgAdmin() ? 8 : 12}>
                    <Form.Item
                      name="diagnostic_code"
                      label="Diagnostic Code"
                    >
                      <Input
                        className="custom-input"
                        placeholder=""
                        onChange={(e) =>
                          handleFieldChange("diagnostic_code", e.target?.value)
                        }
                      />
                    </Form.Item>
                </Col>
                <Col span={authStore?.isOrgAdmin() ? 8 : 12}>
                    <Form.Item
                      name="category"
                      label="Category"
                      initialValue={constants.defaultIssueTrackerCategory}
                    >
                      <Select
                        className="slct_style"
                        placeholder="Select Category"
                        onChange={(value) =>
                          handleFieldChange("category", value)
                        }
                        getPopupContainer={(trigger) => trigger.parentElement || document.body}
                        dropdownStyle={{ zIndex: 2100 }}
                      >
                        {constants.issueTrackerCategories.map((data: any) => (
                          <Option key={data.value} value={data.value}>{data.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                </Col>
                  {authStore?.isOrgAdmin() &&
                  <Col span={8}>
                    <Form.Item
                      name="contractor"
                      label="Assigned Contractor"
                    >
                      <Select
                        className="slct_style"
                        allowClear
                        onChange={(value) =>
                          handleFieldChange("contractor", value)
                        }
                        placeholder={constants.contractorFieldPlaceholder}
                      >
                        {contractorList?.length &&
                          contractorList?.map((data: any) => {
                            return <Option value={data._id}>{data?.name}</Option>
                          })}
                          <Select.Option value={constants.addNewContractor}>
                            <UserOutlined /> {constants.addNewContractor}
                          </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                }
              </Row>

                <Col span={24}>
                <Form.Item
                  normalize={Utils.trimSpacesInValues}
                  name="issue_description"
                  label="Ticket Description"
                >
                  <Input.TextArea
                    showCount
                    maxLength={800}
                    onChange={(e) =>
                      handleFieldChange("issue_description", e.target?.value)
                    }
                    className="descrptn_bx"
                    placeholder="Enter Description"
                    style={{ height: "50px" }}
                    autoSize={{ minRows: 3, maxRows: 4 }}
                  />
                </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    normalize={Utils.trimSpacesInValues}
                    name="components"
                    label="Components"
                  >
                    <Input.TextArea
                      showCount
                      maxLength={400}
                      onChange={(e) =>
                        handleFieldChange("components", e.target?.value)
                      }
                      className="descrptn_bx"
                      placeholder="Enter Components"
                      style={{ height: "50px" }}
                      autoSize={{ minRows: 2, maxRows: 3 }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    normalize={Utils.trimSpacesInValues}
                    name="recommended_action"
                    label="Recommended Action"
                  >
                    <Input.TextArea
                      showCount
                      maxLength={800}
                      onChange={(e) =>
                        handleFieldChange("recommended_action", e.target?.value)
                      }
                      className="descrptn_bx"
                      placeholder="Enter Recommended Action"
                      style={{ height: "50px" }}
                      autoSize={{ minRows: 3, maxRows: 4 }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    normalize={Utils.trimSpacesInValues}
                    name="timeframe"
                    label="Timeframe"
                  >
                    <Input.TextArea
                      showCount
                      maxLength={400}
                      onChange={(e) =>
                        handleFieldChange("timeframe", e.target?.value)
                      }
                      className="descrptn_bx"
                      placeholder="Enter Timeframe"
                      style={{ height: "50px" }}
                      autoSize={{ minRows: 2, maxRows: 3 }}
                    />
                  </Form.Item>
                </Col>
            </Form>
          </Spin>
        </Card>
        <br />
        <Row gutter={[16, 16]} justify="space-between">
          <Col>
            <Form.Item normalize={Utils.trimSpacesInValues}>
              <Button
                block
                className="cncl-btn issue-ticket-btn issue-ticket-cancel-btn"
                icon={<ArrowLeftOutlined />} 
                onClick={handleBack}
                disabled={issueDetails?.issue_id || fieldSaved ? false : true}
              >
                Insights / Tickets
              </Button>
            </Form.Item>
          </Col>
          <Col span={8} style={{ display: 'flex', gap: '8px' }}>
            <Form.Item normalize={Utils.trimSpacesInValues} style={{ marginBottom: 0, flex: 1 }}>
              <Button block className="cncl-btn issue-ticket-btn issue-ticket-cancel-btn" onClick={handleCancel}>
                Cancel
              </Button>
            </Form.Item>
            <Form.Item
              normalize={Utils.trimSpacesInValues}
              name="saveTicket"
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Button
                block
                className="save-btn issue-ticket-btn issue-ticket-save-btn"
                type="primary"
                onClick={handleSubmit}
                disabled={
                  ((fieldValues?.issue_title)?.trim()  !==
                    (previousFieldValues?.issue_title)?.trim() && form.getFieldValue("issue_title") !== '') ||
                    fieldValues?.issue_priority !==
                    previousFieldValues?.issue_priority ||
                    (fieldValues?.diagnostic_code)?.trim() !==
                    (previousFieldValues?.diagnostic_code)?.trim() ||
                    fieldValues?.category !==
                    previousFieldValues?.category ||
                    (fieldValues?.issue_description)?.trim() !==
                    (previousFieldValues?.issue_description)?.trim() ||
                    (fieldValues?.components)?.trim() !==
                    (previousFieldValues?.components)?.trim() ||
                    (fieldValues?.recommended_action)?.trim() !==
                    (previousFieldValues?.recommended_action)?.trim() ||
                    (fieldValues?.timeframe)?.trim() !==
                    (previousFieldValues?.timeframe)?.trim() ||
                    fieldValues?.created_on !==
                    previousFieldValues?.created_on ||
                    fieldValues?.planned_completion_date !==
                    previousFieldValues?.planned_completion_date ||
                    (fieldValues?.contractor !==
                    previousFieldValues?.contractor)
                    ? false
                    : true
                }
              >
                {issueDetails?.issue_id || fieldSaved
                  ? `Update`
                  : `Create`}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Col>


      <Col span={6} style={{ alignSelf: "flex-end" }} >
        {imageLoading ? (
          <Card
            className={
              fieldSaved
                ? "img-preview-card-no-attchmt"
                : "img-preview-card-no-attchmt-opq"
            }
          >
            <div className="image-container-prv">
              <Spin spinning={imageLoading} size="large"></Spin>
            </div>
          </Card>
        ) : imageList?.length ? (
          <Card
            className="img-preview-card"
          >
            <div className="image-container-prv">
              <div style={{ alignContent: "center" }}>
                <Image
                  src={
                    imageList[storedImageIndex]?.length > 1
                      ? (imageList[storedImageIndex][innerImageIndex] ? imageList[storedImageIndex][innerImageIndex] : noResultsImage)
                      : imageList[storedImageIndex] ? imageList[storedImageIndex] : noResultsImage
                  }
                  key={imageList}
                  preview={{
                    zIndex: 10000002
                  }}
                  alt={`${storedImageIndex}`}
                  className="centrd-img"
                />
                {imageList[storedImageIndex]?.length > 1 ? (
                <Pagination total={innerImageLength[storedImageIndex]} size="small" current={innerImageIndex + 1} 
                className="chartDaysLabel"
                defaultPageSize={1}
                onChange={(target) => setInnerImageIndex(target - 1)}/>
                ) : (
                  ""
                )}
              </div>
            </div>
            <Meta
              className="meta-descrpt-attmnt"
              description={
                attachmentNote[storedNoteIndex]?.length > 35
                  ? seeMore(attachmentNote[storedNoteIndex].substring(0, 35))
                  : attachmentNote[storedNoteIndex]
              }
            />
            <Tooltip title={constants.navigateThroughImages} open={showToolTip}>
            <Pagination total={attachmentsIndex} 
            defaultPageSize={1}
            simple 
            className="pagination-bottom-crd"
            onChange={(target)=> {
                    setStoredImageIndex(target - 1)
                    setStoredNoteIndex(target - 1)
                    setInnerImageIndex(0);
            }}/>
            </Tooltip>
          </Card>
        ) : (
          <Card
            className={
              fieldSaved || isEditReq
                ? "img-preview-card-no-attchmt"
                : "img-preview-card-no-attchmt-opq"
            }
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div className="image-container-prv">
              <img
                src={noAttachmentYet}
                alt={`${storedImageIndex}`}
                className="centered-image"
              />
            </div>

            <Meta
              title="No Attachments Yet !"
              style={{ textAlignLast: "center" }}
            />
          </Card>
        )}

        {canAddTicketAttachments && (
        <Form.Item
          normalize={Utils.trimSpacesInValues}
          name="addAttachment"
        >
          <Button
            block
            type="primary"
            className="issue-ticket-btn issue-ticket-save-btn"
            style={{
              height: "45px",
              marginTop: 20,
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
            onClick={() => setIsModalOpen(true)}
            disabled={issueDetails?.issue_id || fieldSaved ? false : true}
          >
            <PlusOutlined/>{" "}
            Add Attachment
          </Button>
        </Form.Item>
        )}
      </Col>
      {authStore?.isOrgAdmin() &&
      <Col span={24}>
        <div className="text-right mt-4">
          {issueDetails?.issue_id && !fieldSaved && (
            <Button
              key="delete"
              type="primary"
              danger
              onClick={handleDelete}
              className="float-left"
            >
              Delete
            </Button>
          )}
        </div>
      </Col>
      }
      <AttachmentUpload
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        ticketId={ticketId}
        setLoading={setLoading}
        loading={loading}
        fetchIssueDetailsWithId={fetchIssueDetailsWithId}
      />
      <Modal
        className="p-5"
        onCancel={() => setSeeMoreNote(false)}
        closable={false}
        footer={null}
        open={seeMoreNote}
        width={800}
      >
        {attachmentNote[storedNoteIndex]}
      </Modal>
      <ConfirmationModal 
        isVisible={isVisible} 
        setIsVisible={setIsVisible} 
        onConfirmFunction={id ? handleEdit : handleSaveTicket} 
        confirmationMessage={"Are you sure you want to proceed ?"} 
        confirmationTitle={id ? "Update Ticket" : "Create Ticket"} />
      <Modal
        title={`${issueDetails?.system_name || ''} - Open Tickets`}
        open={openTicketsModalVisible}
        onCancel={() => setOpenTicketsModalVisible(false)}
        footer={[
          <div key="footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span style={{ fontWeight: 600 }}>Total Issues: {openTickets?.length || 0}</span>
            <Button key="close" onClick={() => setOpenTicketsModalVisible(false)}>
              Close
            </Button>
          </div>
        ]}
        width={1000}
      >
        <Spin spinning={openTicketsLoading}>
          {openTicketsTableData?.length > 0 ? (
            <Table
              columns={openTicketsColumns}
              dataSource={openTicketsTableData}
              pagination={false}
              scroll={{ y: 400 }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              No open tickets found
            </div>
          )}
        </Spin>
      </Modal>
    </>
  );
};
export default IssueTicketDetails;
